/* eslint-disable no-nested-ternary */
import React, { useState } from "react";
import { Flex } from "../../../components/Flex";
import styled from "styled-components";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { FontSize, Text } from "../../../components/Text";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { Trans, useTranslation } from "react-i18next";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { standardDate } from "../../../utils/Date";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AssigneeBanner } from "../../../components/AssigneeBanner";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { WarningModal } from "../../../components/WarningModal";
import { BaseModuleInstance } from "../../../model/module/types/module-instance/BaseModuleInstance";
import { AtiraToast } from "../../../utils/AtiraToast";
import { moduleActions } from "../../../redux/module/module.slice";
import { entryActions } from "../../../redux/entry/entry.slice";
import { Button } from "../../../components/Button";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { Tooltip } from "../../../utils/local-antd";
import { AtiraEmpty } from "../../../components/AtiraEmpty";
import { DynamicModuleInstanceUpdateDrawer } from "../../modules/module-instance/DynamicModuleInstanceUpdateDrawer";
import { faPencil } from "@fortawesome/free-solid-svg-icons/faPencil";
import { useLocation } from "react-router-dom";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { SpinnerFullScreen } from "../../../components/SpinnerFullScreen";
import { useDrawersContext } from "../../tasks/drawers-context";
import { ModuleInstanceAttachment } from "../../../model/module/types/module-instance-attachment/ModuleInstanceAttachment";
import { ModuleInstanceAttachmentUpdateModal } from "../../modules/module-instance-attachment/ModuleInstanceAttachmentUpdateModal";

const Card = styled(Flex)`
  flex-direction: column;
  border-radius: ${Rounded.md};
  border: ${({ theme }) => `1px solid ${theme.darkSub}`};
  padding: ${Spacing.s};
`;

const TitleContainer = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.m};
  border-bottom: ${({ theme }) => `1px solid ${theme.sub}`};
  padding-bottom: ${Spacing.s};
`;

const Title = styled(Text)`
  p {
    display: inline;
  }
`;

const ActionsWrapper = styled(Flex)`
  gap: ${Spacing.m};
  justify-content: flex-end;
  padding-top: ${Spacing.s};
  margin-top: ${Spacing.s};
  border-top: ${({ theme }) => `1px solid ${theme.sub}`};
`;

const StyledButton = styled(Button)`
  background-color: transparent;
  padding: 0;
  margin: 0;
  font-size: calc(${FontSize.m} + 0.25rem);
`;

export const EntryDetailsDrawerModulesTab: React.FC = () => {
  const [
    deleteInstanceWarningModalVisible,
    setDeleteInstanceWarningModalVisible,
  ] = useState(false);
  const [updateInstanceDrawerVisible, setUpdateInstanceDrawerVisible] =
    useState(false);
  const [deleteInstanceLoading, setDeleteInstanceLoading] = useState(false);
  const [selectedInstance, setSelectedInstance] =
    useState<BaseModuleInstance | null>(null);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { entry, setEntry } = useDrawersContext();

  const moduleDefinitions = useAppSelector(
    moduleSliceSelectors.selectAllModuleDefinitions,
  );
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const entryLoading = useAppSelector(
    entrySliceSelectors.selectCurrentEntryLoading,
  );

  const entryName =
    entry?.fields?.email || entry?.fields?.phone || entry?.fields?.name;

  const isOnInstancePage = pathname.includes("/modules");

  const onDeleteInstance = (instance: BaseModuleInstance) => {
    setSelectedInstance(instance);
    setDeleteInstanceWarningModalVisible(true);
  };

  const onUpdateInstance = (instance: BaseModuleInstance) => {
    setSelectedInstance(instance);
    setUpdateInstanceDrawerVisible(true);
  };

  const deleteInstance = async () => {
    try {
      setDeleteInstanceLoading(true);

      if (!selectedInstance?._id) {
        return;
      }

      if (
        (selectedInstance as ModuleInstanceAttachment)?.parentModuleDefinitionId
      ) {
        await dispatch(
          moduleActions.detachModuleInstanceFromEntry({
            moduleInstanceAttachementId: selectedInstance._id,
            moduleInstanceId: selectedInstance.parentId,
            moduleDefinitionId: (selectedInstance as ModuleInstanceAttachment)
              .parentModuleDefinitionId,
            userId: user._id,
          }),
        ).unwrap();
      } else {
        await dispatch(
          moduleActions.deleteModuleInstance({
            moduleInstanceId: selectedInstance._id,
            moduleDefinitionId: selectedInstance.parentId,
            userId: user._id,
          }),
        ).unwrap();
      }

      const updatedEntry = await dispatch(
        entryActions.getEntryById({ entryId: entry?._id!, userId: user._id }),
      ).unwrap();

      setEntry(updatedEntry);
      setDeleteInstanceWarningModalVisible(false);
      setSelectedInstance(null);

      AtiraToast.success(t("modules.instances.delete.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setDeleteInstanceLoading(false);
    }
  };

  if (!entry) {
    return null;
  }

  return (
    <Flex flex={1} flexDirection="column" gap="m">
      {entry.moduleInstancesAttachments?.length ? (
        entry.moduleInstancesAttachments?.map((instanceAttachment) => {
          const currentModule = moduleDefinitions?.find(
            (module) =>
              module._id === instanceAttachment.parentModuleDefinitionId,
          );
          const isFieldsEmpty =
            Object.values(instanceAttachment.fields).every((value) => !value) &&
            !instanceAttachment?.name;
          const currentModulePermissions = user?.modulePermissions?.find(
            (mp) =>
              mp.moduleDefinitionId ===
              instanceAttachment.parentModuleDefinitionId,
          )?.permissions;

          const canUpdate = currentModulePermissions?.UPDATE === true;
          const canDelete = currentModulePermissions?.DELETE === true;

          return (
            <Card key={instanceAttachment._id}>
              <TitleContainer>
                <Title>
                  <Trans
                    i18nKey={"entry.details.modules.moduleName"}
                    values={{ moduleName: currentModule?.name }}
                    components={[
                      <strong />,
                      <Text fontWeight="bold" wordBreak="break-word" />,
                    ]}
                  />
                </Title>

                <AssigneeBanner
                  assignees={members?.filter((m) =>
                    entry.assigneeIds?.includes(m._id),
                  )}
                />
              </TitleContainer>

              <Flex flexDirection="column" gap="m" marginTop="s">
                {instanceAttachment?.name ? (
                  <Text fontSize="m">
                    <strong>{t("modules.instance.name.title")}: </strong>
                    <span>{instanceAttachment.name}</span>
                  </Text>
                ) : null}

                {!isFieldsEmpty ? (
                  <Flex flexDirection="column" gap="m">
                    <Flex flexDirection="column" gap="s">
                      {currentModule?.inputs?.map((input) => {
                        const fieldValue =
                          instanceAttachment.fields?.[input.name];

                        return fieldValue ? (
                          <Text fontSize="s" key={input._id}>
                            <strong>{input.label}: </strong>
                            <span>
                              {input.type === InputTypes.DATE
                                ? standardDate<string>(fieldValue as Date, user)
                                : fieldValue.toString() || ""}
                            </span>
                          </Text>
                        ) : null;
                      })}
                    </Flex>
                  </Flex>
                ) : (
                  <Text align="center" color="darkerSub" fontSize="s">
                    {t("modules.instances.fields.empty")}
                  </Text>
                )}
              </Flex>

              {!isOnInstancePage && (canUpdate || canDelete) ? (
                <ActionsWrapper>
                  {canUpdate ? (
                    <Tooltip title={t("common.update")}>
                      <StyledButton
                        icon={faPencil}
                        onClick={() => onUpdateInstance(instanceAttachment)}
                        color="main"
                      />
                    </Tooltip>
                  ) : null}
                  {canDelete ? (
                    <Tooltip title={t("common.delete")}>
                      <StyledButton
                        icon={faTrash}
                        onClick={() => onDeleteInstance(instanceAttachment)}
                        color="danger"
                      />
                    </Tooltip>
                  ) : null}
                </ActionsWrapper>
              ) : null}
            </Card>
          );
        })
      ) : entry.moduleInstances?.length ? (
        entry.moduleInstances?.map((instance) => {
          const currentModule = moduleDefinitions?.find(
            (module) => module._id === instance.parentId,
          );
          const isFieldsEmpty =
            Object.values(instance.fields).every((value) => !value) &&
            !instance.name;
          const currentModulePermissions = user?.modulePermissions?.find(
            (mp) => mp.moduleDefinitionId === instance.parentId,
          )?.permissions;

          const canUpdate = currentModulePermissions?.UPDATE === true;
          const canDelete = currentModulePermissions?.DELETE === true;

          return (
            <Card key={instance._id}>
              <TitleContainer>
                <Title>
                  <Trans
                    i18nKey={"entry.details.modules.moduleName"}
                    values={{ moduleName: currentModule?.name }}
                    components={[
                      <strong />,
                      <Text fontWeight="bold" wordBreak="break-word" />,
                    ]}
                  />
                </Title>

                <AssigneeBanner
                  assignees={members?.filter((m) =>
                    entry.assigneeIds?.includes(m._id),
                  )}
                />
              </TitleContainer>

              <Flex flexDirection="column" gap="m" marginTop="s">
                {!isFieldsEmpty ? (
                  <Flex flexDirection="column" gap="m">
                    <Flex flexDirection="column" gap="s">
                      {currentModule?.inputs?.map((input) => {
                        const fieldValue = instance.fields?.[input.name];

                        return fieldValue ? (
                          <Text fontSize="s" key={input._id}>
                            <strong>{input.label}: </strong>
                            <span>
                              {input.type === InputTypes.DATE
                                ? standardDate<string>(fieldValue as Date, user)
                                : fieldValue.toString() || ""}
                            </span>
                          </Text>
                        ) : null;
                      })}
                    </Flex>
                  </Flex>
                ) : (
                  <Text align="center" color="darkerSub" fontSize="s">
                    {t("modules.instances.fields.empty")}
                  </Text>
                )}
              </Flex>

              {!isOnInstancePage && (canUpdate || canDelete) ? (
                <ActionsWrapper>
                  {canUpdate ? (
                    <Tooltip title={t("common.update")}>
                      <StyledButton
                        icon={faPencil}
                        onClick={() => onUpdateInstance(instance)}
                        color="main"
                      />
                    </Tooltip>
                  ) : null}
                  {canDelete ? (
                    <Tooltip title={t("common.delete")}>
                      <StyledButton
                        icon={faTrash}
                        onClick={() => onDeleteInstance(instance)}
                        color="danger"
                      />
                    </Tooltip>
                  ) : null}
                </ActionsWrapper>
              ) : null}
            </Card>
          );
        })
      ) : entryLoading ? (
        <SpinnerFullScreen />
      ) : (
        <AtiraEmpty description={t("modules.instances.empty")} />
      )}

      <WarningModal
        title={t("modules.instances.delete.modal.title", {
          entry: entryName,
        })}
        description={t("modules.instances.delete.modal.description")}
        onConfirm={deleteInstance}
        isOpen={deleteInstanceWarningModalVisible}
        onClose={() => setDeleteInstanceWarningModalVisible(false)}
        loading={deleteInstanceLoading}
      />

      {(selectedInstance as ModuleInstanceAttachment)
        ?.parentModuleDefinitionId ? (
        <ModuleInstanceAttachmentUpdateModal
          open={updateInstanceDrawerVisible}
          onClose={() => setUpdateInstanceDrawerVisible(false)}
          attachment={selectedInstance as ModuleInstanceAttachment}
        />
      ) : (
        <DynamicModuleInstanceUpdateDrawer
          open={updateInstanceDrawerVisible}
          onClose={() => setUpdateInstanceDrawerVisible(false)}
          instance={selectedInstance}
        />
      )}
    </Flex>
  );
};
